import { useState, useRef } from 'react';
import { Form, Input, Message, Modal } from '@bedrock/components';
import React, { ReactNode } from 'react';
import withFunctionCall from '#utils/withFunctionCall';
import { ButtonProps, ButtonType } from '@bedrock/components/lib/Button/Button';
import { ModalProps } from '@bedrock/components/lib/Modal';

export interface SubmitModalConfig {
  /** 弹框标题 */
  title?: string;
  /** 按钮文字 */
  okText?: string;
  /** 按钮类型，如 'primary' | 'danger' 等 */
  okType?: ButtonType;
  /** 按钮额外配置 */
  okButtonProps?: ButtonProps;
  /** 表单标签文字 */
  label?: string;
  /** 顶部自定义内容 */
  topShow?: ReactNode;
  /** 提交回调 */
  onCommit?: ({ reason }: { reason: string }) => any;
  /** 是否必填 */
  isRequired?: boolean;
  /** 错误提示信息 */
  errorMessage?: string;
  /** 弹框宽度 */
  width?: number;
  /** 弹框高度 */
  height?: number;
  /** 文本域最大长度 */
  maxLength?: number;
  /** 是否显示字数统计 */
  showCount?: boolean;
  /** 取消按钮文字 */
  cancelText?: string;
  /** 弹框层级 */
  zIndex?: number;
}

const defaultConfig: Required<Omit<SubmitModalConfig, 'topShow' | 'onCommit' | 'okButtonProps'>> & {
  topShow: ReactNode;
  onCommit: ({ reason }: { reason: string }) => void;
  okButtonProps: ButtonProps;
} = {
  title: '提交',
  okText: '提交',
  okType: 'primary',
  label: '说明原因',
  topShow: null,
  onCommit: () => {},
  isRequired: true,
  errorMessage: '提交失败，请填写说明原因',
  width: 480,
  height: 304,
  maxLength: 200,
  showCount: true,
  cancelText: '取消',
  zIndex: 1000,
  okButtonProps: {},
};

// 内部 Modal 组件
type SubmitModalComponentProps = Omit<
  ModalProps,
  | 'onOk'
  | 'title'
  | 'okText'
  | 'cancelText'
  | 'width'
  | 'height'
  | 'zIndex'
  | 'okButtonProps'
> &
  SubmitModalConfig;

const SubmitModalComponent: React.FC<SubmitModalComponentProps> = ({
  title,
  okText,
  okType,
  okButtonProps,
  label,
  topShow,
  onCommit,
  isRequired,
  errorMessage,
  width,
  height,
  maxLength,
  showCount,
  cancelText,
  zIndex,
  onCancel,
  ...modalProps
}) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { validateFields } = form;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeCallbackRef = useRef<(() => void) | undefined>(onCancel as () => void);

  const finalConfig = {
    title: title ?? defaultConfig.title,
    okText: okText ?? defaultConfig.okText,
    okType: okType ?? defaultConfig.okType,
    okButtonProps: {
      ...defaultConfig.okButtonProps,
      ...okButtonProps,
    },
    label: label ?? defaultConfig.label,
    topShow: topShow ?? defaultConfig.topShow,
    onCommit: onCommit ?? defaultConfig.onCommit,
    isRequired: isRequired ?? defaultConfig.isRequired,
    errorMessage: errorMessage ?? defaultConfig.errorMessage,
    width: width ?? defaultConfig.width,
    height: height ?? defaultConfig.height,
    maxLength: maxLength ?? defaultConfig.maxLength,
    showCount: showCount ?? defaultConfig.showCount,
    cancelText: cancelText ?? defaultConfig.cancelText,
    zIndex: zIndex ?? defaultConfig.zIndex,
  };

  const handleOk = async () => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    try {
      const { reason } = await validateFields();
      if (!reason) {
        if (!finalConfig.isRequired) {
          setLoading(true);
          try {
            await Promise.resolve(finalConfig.onCommit({ reason: '' }));
            closeCallbackRef.current?.();
          } finally {
            setLoading(false);
          }
          return;
        } else {
          Message.error({
            content: finalConfig.errorMessage,
            colored: true,
            duration: 3,
            closable: true,
          });
          return;
        }
      } else {
        setLoading(true);
        try {
          await Promise.resolve(finalConfig.onCommit({ reason }));
          closeCallbackRef.current?.();
        } finally {
          setLoading(false);
        }
      }
    } catch (error) {
      setLoading(false);
      // 表单验证失败时不关闭弹框
    }
  };

  const handleCancel = () => {
    closeCallbackRef.current?.();
  };

  return (
    <Modal
      {...modalProps}
      title={finalConfig.title}
      width={finalConfig.width}
      height={finalConfig.height}
      zIndex={finalConfig.zIndex}
      okText={finalConfig.okText}
      cancelText={finalConfig.cancelText}
      okButtonProps={{
        loading,
        type: finalConfig.okType,
        ...finalConfig.okButtonProps,
      }}
      onOk={handleOk}
      onCancel={handleCancel}
    >
      <Form layout="vertical" showLabelColon form={form}>
        {finalConfig.topShow}
        <Form.Item name={'reason'} label={finalConfig.label} required={finalConfig.isRequired}>
          <Input.TextArea
            style={{ width: '100%' }}
            maxLength={finalConfig.maxLength}
            showCount={finalConfig.showCount}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// 使用 withFunctionCall 包装 Modal 组件
export const SubmitModal = withFunctionCall(SubmitModalComponent);
